import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { FuelService } from 'src/app/services/fuel.service';
import { TankService } from 'src/app/services/tank.service';
import { UserService } from 'src/app/services/user.service';
import { Fuel } from 'src/app/shared/models/Fuel';
import { Tank } from 'src/app/shared/models/Tank';
import { User } from 'src/app/shared/models/User';

@Component({
  selector: 'app-tank-page',
  templateUrl: './tank-page.component.html',
  styleUrls: ['./tank-page.component.css']
})
export class TankPageComponent implements OnInit {
  tankForm: FormGroup;
  titulo = 'Crear Tanque';
  id: number;
  fuel_selected: number;
  operator_selected: number[] = [];
  admin_selected: number[] = [];

  webAccessEnabled = true;
  epsEnabled = false;
  observaciones = '';

  currentUser: User;
  adminsList: User[] = [];
  selectedAdmins: User[] = [];
  adminsSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    enableCheckAll: false
  };

  operatorsList: User[] = [];
  selectedOperators: User[] = [];
  operatorsSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    enableCheckAll: false
  };

  byVehicle = false;
  fuelList: Fuel[] = [];
  selectedFuel: Fuel[] = [];

  fuelSettings = {
    singleSelection: true,
    idField: 'id',
    textField: 'name',
    enableCheckAll: false
  };


  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tankService: TankService,
    private userService: UserService,
    private fuelService: FuelService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.tankForm = this.initTankForm();
    this.id = parseInt(this.activatedRoute.snapshot.paramMap.get('id'), 10);
  }


  ngOnInit() {
    this.currentUser = this.userService.currentUser;

    if (this.id) {
      this.initializeEditMode();
    } else {
      this.initTankForm();
    }
  }

  get isSuperadmin(): boolean {
    return this.currentUser && this.currentUser.role === 'superadmin';
  }

  saveTank() {
    if (this.tankForm.invalid) {
      return;
    }

    const tank = this.buildTankPayload();

    const saveOrUpdate = this.id
      ? this.tankService.editTank(this.id, tank)
      : this.tankService.saveTank(tank);

    saveOrUpdate.subscribe(
      (savedTank) => {
        this.syncCurrentTankIfNeeded(savedTank || tank);
        this.router.navigate(['/tanks']);
      },
      (error) => {
        console.log(error);
        this.tankForm.reset();
      }
    );
  }

  private buildTankPayload(): Tank {
    const formValue = this.tankForm.value;

    const tank: Tank = {
      name: formValue.name,
      capacity: formValue.capacity,
      available: formValue.available,
      byVehicle: formValue.byVehicle,
      showAvailable: formValue.showAvailable,
      vehicleUseInputAllowed: formValue.vehicleUseInputAllowed,
      encoderSpeed: formValue.encoderSpeed,
      caudalRate: formValue.caudalRate,
      automaticPumpCuttingEnabled: formValue.automaticPumpCuttingEnabled,
      printerEnabled: formValue.printerEnabled,
      automaticPumpCuttingTime: formValue.automaticPumpCuttingTime,
      filterLife: formValue.filterLife,
      amountFilter: formValue.amountFilter,
      historicalData: formValue.historicalData,
      historicalDataText: formValue.historicalDataText,
      historicalDischargesAmount: formValue.historicalDischargesAmount,
      availableAlert: formValue.availableAlert,
      multiTankVehicle: formValue.multiTankVehicle,
      aprox_by_litros: formValue.aprox_by_litros,
      fuel_name: '',
      operators: this.selectedOperators.map(operator => operator.id),
      administrators: this.selectedAdmins.map(admin => admin.id),
      epsEnabled: this.epsEnabled,
      webAccessEnabled: this.webAccessEnabled,
    } as Tank;

    if (this.isSuperadmin) {
      tank.observaciones = this.observaciones != null ? String(this.observaciones) : '';
    }

    if (this.selectedFuel.length > 0) {
      tank.fuel_name = this.selectedFuel[0].name;
      tank.fuel = this.selectedFuel[0].id;
    }

    return tank;
  }

  private initTankForm(): FormGroup {
    if (this.id) {
      this.userService.getAllAdmins(this.id).subscribe((adminsList: User[]) => {
        this.adminsList = adminsList.filter(admin => admin.tanks.includes(this.id));
      });

      this.userService.getAlloperators(this.id).subscribe((operatorsList: User[]) => {
        this.operatorsList = operatorsList.filter(operator => operator.tanks.includes(this.id));
      });
      this.fuelService.getFuelByTank(this.id).subscribe((fuelList: Fuel[]) => {
        this.fuelList = fuelList;
      });
    }

    return this.fb.group({
      id: [''],
      name: [''],
      available: [''],
      capacity: [''],
      fuel: [null],
      byVehicle: [false],
      fuelId: [],
      administrators: [[]],
      operators: [[]],
      showAvailable: [true],
      vehicleUseInputAllowed: [true],
      encoderSpeed: [''],
      caudalRate: [''],
      automaticPumpCuttingEnabled: [true],
      printerEnabled: [true],
      automaticPumpCuttingTime: [''],
      filterLife: [''],
      amountFilter: [''],
      historicalData: [''],
      historicalDataText: [''],
      historicalDischargesAmount: [''],
      availableAlert: [''],
      multiTankVehicle: [''],
      aprox_by_litros: [false],
    });
  }

  private initializeEditMode() {
    if (!this.id) {
      return;
    }

    forkJoin(
      this.tankService.getTank(this.id),
      this.fuelService.getFuelByTank(this.id),
      this.userService.getAllAdmins(this.id),
      this.userService.getAlloperators(this.id),
    ).subscribe(([tank, fuelList, adminsList, operatorsList]: [Tank, Fuel[], User[], User[]]) => {
      this.fuelList = fuelList;
      this.adminsList = adminsList.filter((admin) => admin.tanks.includes(this.id));
      this.operatorsList = operatorsList.filter((operator) => operator.tanks.includes(this.id));

      this.applyTankToForm(tank);
      this.titulo = 'Editando Tanque ' + tank.name;

      this.selectedFuel = [];
      const tankFuel = fuelList.find((fuel) => fuel.id == tank.fuel);
      if (tankFuel) {
        this.selectedFuel.push(tankFuel);
      }

      this.selectedAdmins = [];
      tank.administrators.forEach((adminId) => {
        const admin = this.adminsList.find((ad) => ad.id === adminId);
        if (admin) {
          this.selectedAdmins.push(admin);
        }
      });

      this.selectedOperators = [];
      tank.operators.forEach((operatorId) => {
        const operator = this.operatorsList.find((op) => op.id === operatorId);
        if (operator) {
          this.selectedOperators.push(operator);
        }
      });

      this.loadAccessFlags(tank);
      if (this.isSuperadmin) {
        this.loadObservaciones(tank);
      }
    });
  }

  private loadObservaciones(data: Tank): void {
    this.observaciones = data.observaciones != null ? String(data.observaciones) : '';
    setTimeout(() => {
      this.observaciones = data.observaciones != null ? String(data.observaciones) : '';
      this.cdr.detectChanges();
    });
  }

  private loadAccessFlags(data: Tank): void {
    this.webAccessEnabled = this.readFlag(data, 'webAccessEnabled', true);
    this.epsEnabled = this.readFlag(data, 'epsEnabled', false)
      || (data as any).externalApiEnabled === true;
    setTimeout(() => {
      this.webAccessEnabled = this.readFlag(data, 'webAccessEnabled', true);
      this.epsEnabled = this.readFlag(data, 'epsEnabled', false)
        || (data as any).externalApiEnabled === true;
      this.cdr.detectChanges();
    });
  }

  private readFlag(data: Tank, key: string, defaultValue: boolean): boolean {
    const value = (data as any)[key];
    if (value === true || value === false) {
      return value;
    }
    return defaultValue;
  }

  private applyTankToForm(data: Tank): void {
    this.tankForm.patchValue(data);
  }

  onObservacionesInput(event: Event): void {
    this.observaciones = (event.target as HTMLTextAreaElement).value;
  }

  onWebAccessEnabledChange(event: Event): void {
    this.webAccessEnabled = (event.target as HTMLInputElement).checked;
  }

  onEpsEnabledChange(event: Event): void {
    this.epsEnabled = (event.target as HTMLInputElement).checked;
  }

  private syncCurrentTankIfNeeded(tank: Tank): void {
    if (!this.id) {
      return;
    }

    const currentTank = this.tankService.currentTank;
    if (currentTank && currentTank.id === this.id) {
      this.tankService.setCurrentTank({
        ...currentTank,
        ...tank,
        webAccessEnabled: this.webAccessEnabled,
        epsEnabled: this.epsEnabled,
        ...(this.isSuperadmin ? { observaciones: this.observaciones } : {}),
      });
    }
  }

  onItemSelect(item: any) {
    console.log(item);
  }

  aproxByLitros(): void {
    if (!this.tankForm.value.fuel[0].cost) {
      alert('Antes de activar por precio debes ingresar un costo de combustible');
      this.tankForm.get('aprox_by_litros').setValue(false);
    }
  }

  onFuelSelect(item: any) {
    this.fuel_selected = item.id;
  }

  onOperatorSelect(item: any) {
    this.operator_selected.push(item);
  }

  onAdminSelect(item: any) {
    this.admin_selected.push(item.id);
  }

  onOperatorDeselect(item: any) {
    const index = this.operator_selected.indexOf(item);
    if (index > -1) {
      this.operator_selected.splice(index, 1);
    }
  }

  onAdminDeselect(item: any) {
    const index = this.admin_selected.indexOf(item.id);
    if (index > -1) {
      this.admin_selected.splice(index, 1);
    }
  }

}
